import { TeamPhotoStack } from "@/grid/components/TeamPhotoStack";
import { getCell } from "@/grid/data/assemble-grid";
import { MetricCell } from "@/grid/components/MetricCell";
import { calculateTeamGrade, getOrdinal } from "@/grid/lib/scorecard";
import { getGridCellLayout } from "@/grid/lib/grid-cell-layout";
import { DEFAULT_THRESHOLDS } from "@/grid/lib/pacing";
import type { DepartmentDefinition, GridTeam } from "@/grid/core/types";
import { cn } from "@/lib/cn";

interface TeamCardProps {
  team: GridTeam;
  department: DepartmentDefinition;
  showLabels?: boolean;
  showValues?: boolean;
  tvMode?: boolean;
  rank?: number;
  rankingCount?: number;
  className?: string;
}

export function TeamCard({
  team,
  department,
  showLabels = false,
  showValues = false,
  tvMode = false,
  rank,
  rankingCount = 3,
  className,
}: TeamCardProps) {
  const layout = getGridCellLayout({ tvMode, showValues, showPeriodLabels: showLabels });
  const grade = calculateTeamGrade(team, department);
  const showRankHighlight = rank !== undefined && rank <= rankingCount;
  const thresholds = department.thresholds ?? DEFAULT_THRESHOLDS;

  return (
    <div
      className={cn(
        "relative flex flex-col items-center rounded-xl",
        layout.cardClassName,
        showRankHighlight && "ring-1",
        showRankHighlight &&
          rank === 1 &&
          "bg-amber-50 ring-amber-300/40 dark:bg-amber-950/20 dark:ring-amber-400/30",
        showRankHighlight &&
          rank === 2 &&
          "bg-slate-50 ring-slate-300/40 dark:bg-slate-800/20 dark:ring-slate-400/30",
        showRankHighlight &&
          rank === 3 &&
          "bg-orange-50 ring-orange-300/40 dark:bg-orange-950/20 dark:ring-orange-400/30",
        showRankHighlight &&
          rank &&
          rank > 3 &&
          "bg-[var(--color-muted)]/30 ring-[var(--color-muted-foreground)]/20",
        className,
      )}
    >
      {showRankHighlight ? (
        <div
          className={cn("pointer-events-none absolute inset-x-0 text-center", layout.rankBadgeTop)}
        >
          <span
            className={cn(
              "inline-block rounded-full font-semibold",
              layout.rankBadgeClass,
              rank === 1 && "bg-amber-400 text-amber-950",
              rank === 2 && "bg-slate-400 text-slate-900",
              rank === 3 && "bg-orange-400 text-orange-950",
              rank &&
                rank > 3 &&
                "bg-[var(--color-muted-foreground)] text-[var(--color-background)]",
            )}
          >
            {getOrdinal(rank!)}
          </span>
        </div>
      ) : null}

      <div className={cn("flex flex-col", layout.metricGap)}>
        {showLabels ? (
          <div className={cn("flex items-center", layout.columnGap)}>
            {department.periods.map((period) => (
              <div
                key={period.id}
                className={cn(
                  layout.labelWidth,
                  "flex items-center justify-center font-medium text-[var(--color-muted-foreground)]/70",
                  layout.legendLabelClass,
                )}
              >
                {period.shortLabel ?? period.label}
              </div>
            ))}
          </div>
        ) : null}

        {department.metrics.map((metric) => (
          <div key={metric.id} className={cn("flex items-center", layout.columnGap)}>
            {department.periods.map((period) => {
              const cell = getCell(team, metric.id, period.id);
              return (
                <MetricCell
                  key={`${metric.id}-${period.id}`}
                  actual={cell?.actual ?? null}
                  target={cell?.target ?? null}
                  pacingTarget={cell?.pacingTarget ?? null}
                  targetSource={cell?.targetSource ?? null}
                  metric={metric}
                  period={period}
                  teamName={team.name}
                  showValues={showValues}
                  tvMode={tvMode}
                  thresholds={thresholds}
                  className={layout.cellSize}
                />
              );
            })}
          </div>
        ))}
      </div>

      <div className={cn("flex flex-col items-center", layout.footerMarginTop, layout.footerGap)}>
        <span className="text-sm font-medium tracking-tight text-[var(--color-muted-foreground)]">
          {team.name}
        </span>
        <TeamPhotoStack teamName={team.name} department={department.id} tvMode={tvMode} />
        <span className="text-lg font-bold tracking-wide text-[var(--color-foreground)]">
          {grade}
        </span>
      </div>
    </div>
  );
}
